import React, { useRef, useState, useCallback, useEffect } from 'react'

import {
    Box
} from '@mui/material';

import DateFilter from '@inovua/reactdatagrid-community/DateFilter'

import { useNavigate, Link } from 'react-router-dom';

import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import DataGridToolbar from '../../common/DataGridToolbar';
import DataGridTable from '../../common/DataGridTable';
import DataGridRowActionMenu from '../../common/DataGridRowActionMenu';
import CommonRestricted from '../../common/CommonRestricted';

import { useAuthAccess } from '../../../hooks/useAuthAccess';

import { icons } from '../../common/CommonIcons';
import { useDataGridParams } from '../../../hooks/useDataGridParams';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useUsers, userTypes, targetTypes } from '../../../hooks/api/useUsers';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

export default function ListSuppliers({
    routeType
}) {

    const { t } = useTranslations({ ns: ["common"] })

    const navigate = useNavigate();
    const { promptMessage } = useSystemPrompt();
    const { list, remove, filterOperatorTypes } = useUsers({ userType: userTypes.supplier });

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnBooleanDisplay, getRenderColumnTextDisplay } = useDataGridParams('id')
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.user.id, scopeTypes.user.supplier.id)

    const { authData } = useAuthAccess()

    const [selectedIds, setSelectedIds] = useState([])
    const [refreshDataGrid, setRefreshDataGrid] = useState(0)

    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const gridRef = useRef();

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    const fetchData = async (props) => {

        resetDataGridTableSelection();

        try {

            const filterDataValues = []
            let filterData = []

            filterData = props.filterValue

            console.log('props', props)

            // filter supplier type to route type
            if (routeType === routeTypes.admin) {
                filterDataValues.push({
                    name: "type",
                    operator: filterOperatorTypes.eq,
                    value: routeTypes.admin
                })
            }
            else if (routeType === routeTypes.retail) {
                filterDataValues.push({
                    name: "type",
                    operator: filterOperatorTypes.eq,
                    value: routeTypes.retail
                })
                filterDataValues.push({
                    name: "retailer_id",
                    operator: filterOperatorTypes.eq,
                    value: authData?.profile?.user?.id
                })
            }

            let mergeFilter = [...filterData, ...filterDataValues]

            props.filterValue = mergeFilter

            const result = await list(props)

            return {
                data: result?.data?.suppliers,
                count: result?.data?.nofilter_total
            }

        } catch (error) {

            promptMessage(false, `Error getting results (${error?.message})`)

            return {
                data: [],
                count: 0
            }
        }
    }

    useEffect(() => {

        defineColumnsView()

    }, [])

    const defineColumnsView = () => {
        defineColumns([
            {
                name: 'username',
                header: t('username'),
                renderValue: ({ value, cellProps }) => {

                    if (isPermissionAllow({ functionType: functionTypes.pageView.id }) === false) {
                        return getRenderColumnTextDisplay(value)
                    }

                    let path = getRoute(`users/supplier/view/${cellProps.data.user_id}`);

                    return getRenderColumnLink(path, value)

                }
            },
            {
                name: 'firstname', header: t('firstName')
            },
            {
                name: 'lastname', header: t('lastName')
            },
            {
                name: 'contact', header: t('contact')
            },
            {
                name: 'email', header: t('email')
            },
            {
                name: 'company_name', header: t('company')
            },
            {
                name: 'status',
                header: t('status'),
                renderValue: ({ cellProps, value }) => getRenderColumnBooleanDisplay(value, [t('enabled'), t('disabled')], ["primary", "default"], "")
            }
        ])
    }

    const handleDelete = async () => {

        try {
            let result = await remove({ ids: selectedIds, targetType: targetTypes.supplier })

            promptMessage(true, result?.message)
            setRefreshDataGrid(currentVal => currentVal + 1)

        } catch (error) {
            promptMessage(false, error?.message)
        }

    }

    const RenderToolbar = () => {

        const getToolbarMainMenus = () => {

            let props = [];

            if (isPermissionAllow({ functionType: functionTypes.pageNew.id })) {
                props.push({
                    displayIconName: icons.plus,
                    onClick: () => {
                        navigate(getRoute("users/supplier/new"));
                    }
                })
            }

            return props
        }

        const getActionOnClick = () => {

            let handleFunction = null

            if (isPermissionAllow({ functionType: functionTypes.remove.id })) {
                handleFunction = handleDelete
            }

            return handleFunction
        }

        return (
            <DataGridToolbar
                selectedRowCount={
                    selectedIds.length
                }
                mainMenus={
                    getToolbarMainMenus()
                }
                actionOnClickRemove={
                    getActionOnClick()
                }
                actionMenus={[]}
            />
        )
    }

    const RenderDataGridTable = () => {

        return (
            <DataGridTable
                ref={gridRef}
                refresh={refreshDataGrid}
                fetchData={fetchData}
                dataGrid={{
                    idProperty: "user_id",
                    style: {
                        minHeight: 600
                    },
                    pageSizes: [20, 50, 100],
                    defaultLimit: 10,
                    defaultSkip: 0,
                    checkboxColumn:
                        isPermissionAllow({ functionType: functionTypes.remove.id }),
                    columns,
                    defaultFilterValue: filterValues,
                    onSelectionChange: (items) => {
                        setSelectedIds(
                            items
                        )
                    }
                }}

            />
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.suppliers })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t('suppliers')}
                breadcrumbs={[
                    {
                        displayText: t('suppliers'),
                        props: {
                            // underline: "hover",
                        }
                    }
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageList.id }) ?
                    <>
                        {
                            RenderToolbar()
                        }
                        {
                            RenderDataGridTable()
                        }
                    </>
                    :
                    (
                        <CommonRestricted />
                    )
            }

        </Box>
    )
}
